import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Newspaper } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return (iso || "").slice(0, 10); }
}
function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function LatestBlogSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog?per_page=3`);
        if (!cancelled) setPosts(data.items || []);
      } catch { /* silent — section just hides on error */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Don't render the section if there's nothing to show
  if (loading || posts.length === 0) return null;

  return (
    <section className="bg-[#3915AA] py-20 sm:py-24" data-testid="home-latest-blog">
      <div className="container-custom">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass mb-3">
              <Newspaper className="w-3.5 h-3.5 text-[#C9BCFF]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/70">From the journal</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Grooming tips &amp; pet care
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">
              Practical advice for Bhubaneswar pet parents — from our groomers and the 500+ pets we see every month.
            </p>
          </div>
          <Link
            to="/blog"
            data-testid="home-blog-view-all"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#FC5106] hover:text-[#FF7A3D]"
          >
            View all posts <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`home-blog-card-${p.slug}`}
              className="group rounded-2xl overflow-hidden bg-[#2E0F94] border border-white/10 hover:border-[#FC5106]/40 transition-colors"
            >
              <div className="aspect-[16/10] overflow-hidden bg-[#1F0A66]/40">
                {p.featured_image_url ? (
                  <img
                    src={resolveImg(p.featured_image_url)}
                    alt={p.featured_image_alt || p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#FC5106]/30 to-white/20" />
                )}
              </div>
              <div className="p-5 space-y-3">
                <div className="flex items-center gap-2 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" />{fmt(p.published_at || p.created_at)}</span>
                  <span>·</span>
                  <span className="uppercase tracking-wider text-[#C9BCFF]">{p.category || "General"}</span>
                </div>
                <h3 className="font-heading font-bold text-lg text-white group-hover:text-[#FC5106] transition-colors line-clamp-2">
                  {p.title}
                </h3>
                <p className="text-white/60 text-sm line-clamp-3">{p.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-sm text-[#FC5106] font-semibold">
                  Read more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
